import {
  blendHistoryInputSchema,
  calculateBlend,
  type BlendCalculationOutcome,
  type BlendCalculationRequest,
  type BlendHistoryEntry,
} from '@shared';
import { createClientId } from './createClientId';
import {
  LOCAL_BLEND_HISTORY_STORE,
  openLocalProductsDb,
  requestToPromise,
  txDone,
  type IdbFactory,
} from './productRepository/localProductsDb';

export interface BlendHistoryStore {
  list(): Promise<BlendHistoryEntry[]>;
  put(entry: BlendHistoryEntry): Promise<void>;
  delete(id: string): Promise<boolean>;
}

export function createIdbBlendHistoryStore(options?: {
  indexedDB?: IdbFactory;
}): BlendHistoryStore {
  const factory = options?.indexedDB;
  return {
    async list() {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_BLEND_HISTORY_STORE, 'readonly');
        const rows = await requestToPromise(
          tx.objectStore(LOCAL_BLEND_HISTORY_STORE).getAll(),
        );
        await txDone(tx);
        return (rows as BlendHistoryEntry[]).sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );
      } finally {
        db.close();
      }
    },
    async put(entry) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_BLEND_HISTORY_STORE, 'readwrite');
        tx.objectStore(LOCAL_BLEND_HISTORY_STORE).put(entry);
        await txDone(tx);
      } finally {
        db.close();
      }
    },
    async delete(id) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_BLEND_HISTORY_STORE, 'readwrite');
        const store = tx.objectStore(LOCAL_BLEND_HISTORY_STORE);
        const existing = await requestToPromise(store.get(id));
        if (existing === undefined) {
          await txDone(tx);
          return false;
        }
        store.delete(id);
        await txDone(tx);
        return true;
      } finally {
        db.close();
      }
    },
  };
}

export function createMemoryBlendHistoryStore(): BlendHistoryStore {
  const rows = new Map<string, BlendHistoryEntry>();
  return {
    async list() {
      return [...rows.values()].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      );
    },
    async put(entry) {
      rows.set(entry.id, entry);
    },
    async delete(id) {
      return rows.delete(id);
    },
  };
}

function calculate(request: BlendCalculationRequest): BlendCalculationOutcome {
  return calculateBlend(request);
}

export function createLocalBlendsApi(options?: {
  store?: BlendHistoryStore;
  createId?: () => string;
  now?: () => Date;
}) {
  const store = options?.store ?? createIdbBlendHistoryStore();
  const createId = options?.createId ?? createClientId;
  const now = options?.now ?? (() => new Date());
  return {
    async calculate(request: BlendCalculationRequest) {
      return calculate(request);
    },
    async history() {
      return store.list();
    },
    async save(request: BlendCalculationRequest, note?: string) {
      const parsed = blendHistoryInputSchema.parse({ request, note });
      const outcome = calculate(parsed.request);
      if (!outcome.result) {
        throw new Error('Cannot save a blend whose target weight is not measurable.');
      }
      const entry: BlendHistoryEntry = {
        id: createId(),
        createdAt: now().toISOString(),
        note: parsed.note ?? null,
        request: parsed.request,
        result: outcome.result,
      };
      await store.put(entry);
      return entry;
    },
    async deleteHistory(id: string) {
      return { deleted: await store.delete(id) };
    },
  };
}
